import { useRef } from 'react';
import { isGameSeed, type GameSeed } from '@orborix/shared-types';
import { type ScenarioMission, type ScenarioStartContext } from '../../scenarios/scenarioTypes.js';
import { parseListenStart, type ListenStartContext } from '../../listen/conversationBridge.js';
import { parseShortStart, type ShortStartContext } from '../../orbitas/conversationBridge.js';

/**
 * Contexto de ARRANQUE de la conversación: todo lo que viaja por
 * `location.state` desde otras pantallas (historial, briefing de escenario,
 * player de Escuchar, onboarding, Programa). Se lee UNA sola vez al montar —
 * `useVoiceSession` fija los hints iniciales con estos valores y no deben
 * cambiar aunque el router re-renderice.
 */
export interface ConversationStartContext {
  /** "Continuar conversación" desde el historial (UUID validado). */
  resumeSessionId: string | null;
  /** Escenario (roleplay con misión) si se entró desde el briefing. */
  scenario: ScenarioStartContext | null;
  /** Misión visible en el tracker en vivo (sólo curados con objetivos). */
  scenarioMission: ScenarioMission | null;
  /** Título/emoji del escenario para el header de la escena. */
  scenarioLabel: { title: string; emoji: string } | null;
  /** Dirección "Escuchar" (Fase 19): capítulo escuchado → FOCO de la charla. */
  listenStart: ListenStartContext | null;
  /**
   * Dirección "Órbitas" (plan 44 F1b): las cards del feed que el user acaba de
   * ver ("Charlar de esto" / "Usalo en una charla") → FOCO de la charla.
   */
  shortStart: ShortStartContext | null;
  /** Examen de nivelación opt-in (Fase C) → mode 'calibration'. */
  examMode: boolean;
  /** Programa guiado (fase 15): fuerza mode 'plan' + la unit elegida. */
  overrideUnitId: string | null;
  /** Sub-unidad puntual elegida (panel/reader → "Practicar", fase 17.B). */
  overrideSubUnitId: string | null;
  /**
   * Charla guiada A0 (plan 33): fuerza `mode='a0'` + la misión. `isBoss` = es el
   * examen-gate de graduación (al completar → `/a0/boss-result` + graduación);
   * sin él es una micro-charla opcional (al salir → vuelve a /home). `null` =
   * no es una charla A0.
   */
  a0Mission: { missionId: string; isBoss: boolean } | null;
  /** Título de la unidad activa (modo plan) para el pill del header. */
  planLabel: { title: string } | null;
  /** Objetivos outcome-framed de la unit activa (tracker en vivo, fase 16.B). */
  planObjectives: readonly string[];
  /**
   * Corresponde la línea de intro del Programa (`programIntro.ts`): sesión nueva
   * de una unit a la que se entró SIN pasar por la pantalla del Programa (ésa
   * marca `fromProgram` en el state, porque ahí el user ya vio la tarjeta).
   */
  programIntroEligible: boolean;
  /**
   * Juego con el que abre la charla («Armá tu nave», pieza Motores): apenas la sesión está
   * conectada se elige solo, igual que desde el menú «¿De qué hablamos?». `null` = charla normal.
   */
  autoSeed: GameSeed | null;
  /**
   * La charla viene de la pieza Cabina de «Armá tu nave», que promete «Contale
   * al coach quién sos y para qué querés el inglés»: el coach abre pidiendo eso
   * (hint `opener: 'get-to-know'`). Sólo en una charla libre nueva.
   */
  getToKnow: boolean;
}

/**
 * Lee `a0Mission` del query string (`?a0mission=<id>&a0boss=1`) como entrada
 * DEEP-LINKABLE de la charla a0 (plan 33). Es la entrada para links externos /
 * el "hito" del sendero; el `location.state` (oferta del cierre) tiene prioridad.
 * Seguro: `mode='a0'` es no-persistible → un A0 que deep-linkea NO se auto-promueve
 * (no es la puerta trasera del A0Gated original).
 */
export function parseA0MissionFromSearch(
  search: string,
): { missionId: string; isBoss: boolean } | null {
  try {
    const params = new URLSearchParams(search);
    const missionId = params.get('a0mission');
    if (!missionId) return null;
    return { missionId, isBoss: params.get('a0boss') === '1' };
  } catch {
    return null;
  }
}

/**
 * Parser puro de `location.state` (+ el `search` para la entrada deep-linkable de
 * la charla a0). Validamos acá también (defense-in-depth; RLS protege a nivel DB):
 * UUID para el resume, tipos para el resto.
 */
export function parseStartContext(state: unknown, search = ''): ConversationStartContext {
  const s = state as {
    resumeSessionId?: unknown;
    scenario?: ScenarioStartContext;
    exam?: unknown;
    overrideUnitId?: unknown;
    overrideSubUnitId?: unknown;
    unitTitle?: unknown;
    unitObjectives?: unknown;
    fromProgram?: unknown;
    a0Mission?: { missionId?: unknown; isBoss?: unknown };
    seed?: unknown;
    shipPiece?: unknown;
  } | null;

  const resumeSessionId =
    typeof s?.resumeSessionId === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.resumeSessionId)
      ? s.resumeSessionId
      : null;

  // Escenario: curado → `scenarioId`; generado → `inlineScenario` (JSON, que el
  // wsServer parsea). La misión (sólo curados) alimenta el tracker en vivo.
  const scenario = s?.scenario ?? null;

  const overrideUnitId =
    typeof s?.overrideUnitId === 'string' && s.overrideUnitId.length > 0 ? s.overrideUnitId : null;
  const overrideSubUnitId =
    typeof s?.overrideSubUnitId === 'string' && s.overrideSubUnitId.length > 0
      ? s.overrideSubUnitId
      : null;

  // Título de la unidad activa: viaja desde el ChapterSheet/RecoSheet/debrief;
  // si falta, mostramos un genérico igual (lo importante es que NO parezca
  // charla libre).
  const planLabel = overrideUnitId
    ? {
        title:
          typeof s?.unitTitle === 'string' && s.unitTitle.length > 0
            ? s.unitTitle
            : 'Unidad del programa',
      }
    : null;

  // Objetivos de la unit: vacío si entramos sin la lista (ej. "seguir con" del
  // debrief) → no se muestra el tracker, pero la sesión funciona igual.
  const planObjectives: readonly string[] = overrideUnitId
    ? Array.isArray(s?.unitObjectives)
      ? s.unitObjectives.filter((o): o is string => typeof o === 'string')
      : []
    : [];

  // Charla guiada A0 (plan 33): misión válida → fuerza mode='a0'. `missionId`
  // no vacío; `isBoss` opt-in (examen-gate). El `location.state` (oferta del
  // cierre) gana; si no, cae al query (`?a0mission=`, deep-link). Id desconocido
  // lo valida el backend (degrada a charla a0 sin misión).
  const a0Mission =
    typeof s?.a0Mission?.missionId === 'string' && s.a0Mission.missionId.length > 0
      ? { missionId: s.a0Mission.missionId, isBoss: s.a0Mission.isBoss === true }
      : parseA0MissionFromSearch(search);

  return {
    resumeSessionId,
    scenario,
    scenarioMission: scenario?.mission ?? null,
    scenarioLabel: scenario ? { title: scenario.title, emoji: scenario.emoji } : null,
    listenStart: parseListenStart(state),
    shortStart: parseShortStart(state),
    examMode: s?.exam === true,
    overrideUnitId,
    overrideSubUnitId,
    planLabel,
    planObjectives,
    programIntroEligible:
      overrideUnitId != null && resumeSessionId == null && s?.fromProgram !== true,
    a0Mission,
    // Sólo en charla libre: en una lección, escena, examen o charla A0 el tema lo marca otra cosa.
    autoSeed:
      isGameSeed(s?.seed) && !overrideUnitId && !scenario && s?.exam !== true && !a0Mission
        ? s.seed
        : null,
    getToKnow:
      s?.shipPiece === 'cabina' &&
      resumeSessionId == null &&
      !overrideUnitId &&
      !scenario &&
      s.exam !== true &&
      !a0Mission &&
      s.seed == null,
  };
}

/** Congela el start context del primer render (one-shot, como los viejos refs). */
export function useStartContext(state: unknown): ConversationStartContext {
  const ref = useRef<ConversationStartContext | null>(null);
  ref.current ??= parseStartContext(
    state,
    typeof window !== 'undefined' ? window.location.search : '',
  );
  return ref.current;
}
